import React from 'react';
import { ChevronDown, ExternalLink, Globe, ShieldCheck } from 'lucide-react';
import styled from 'styled-components';
import type { EventInfoSource } from '../../types/api';
import { borderRadius, colors, spacing, typography } from '../../styles/theme';
import { getSourceLabel, toAbsoluteUrl } from '../../utils/externalUrl';
import { DetailSection } from './DetailSection';

const Disclosure = styled.details`
  &[open] .disclosure-chevron {
    transform: rotate(180deg);
  }
`;

const Summary = styled.summary`
  min-height: 44px;
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  color: ${colors.text.primary};
  cursor: pointer;
  font-size: ${typography.fontSizes.xl};
  font-weight: ${typography.fontWeights.semibold};
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }

  .disclosure-chevron {
    margin-left: auto;
    transition: transform 0.15s ease;
  }
`;

const Provenance = styled.div`
  margin: ${spacing[2]} 0 ${spacing[4]};
  color: ${colors.text.tertiary};
  font-size: ${typography.fontSizes.xs};
`;

const SourceList = styled.ul`
  display: grid;
  gap: ${spacing[3]};
  margin: 0;
  padding: 0;
  list-style: none;
`;

const SourceItem = styled.li`
  min-width: 0;
  padding-top: ${spacing[3]};
  border-top: 1px solid ${colors.border.light};
`;

const SourceLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  max-width: 100%;
  color: ${colors.primary[700]};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.semibold};
  text-decoration: none;
  overflow-wrap: anywhere;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`;

const SourceMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[1]} ${spacing[2]};
  margin-top: ${spacing[1]};
  color: ${colors.text.tertiary};
  font-size: ${typography.fontSizes.xs};
`;

const TrustBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px ${spacing[2]};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.full};
  background: ${colors.background.tertiary};
  color: ${colors.text.secondary};
`;

function formatTimestamp(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(date);
}

export interface EventSourcesDisclosureProps {
  sources?: EventInfoSource[];
  firstExtractedAt?: string | null;
  lastRefreshedAt?: string | null;
  onToggle?: (open: boolean) => void;
}

export const EventSourcesDisclosure: React.FC<EventSourcesDisclosureProps> = ({
  sources = [],
  firstExtractedAt,
  lastRefreshedAt,
  onToggle,
}) => {
  if (sources.length === 0 && !firstExtractedAt && !lastRefreshedAt) return null;
  const timestamps = [
    firstExtractedAt && `First found ${formatTimestamp(firstExtractedAt)}`,
    lastRefreshedAt && `Last refreshed ${formatTimestamp(lastRefreshedAt)}`,
  ].filter(Boolean);

  return (
    <DetailSection id="event-sources">
      <Disclosure onToggle={event => onToggle?.(event.currentTarget.open)}>
        <Summary aria-label={`Sources (${sources.length})`}>
          <Globe size={19} aria-hidden="true" />
          Sources ({sources.length})
          <ChevronDown className="disclosure-chevron" size={18} aria-hidden="true" />
        </Summary>
        {timestamps.length > 0 && <Provenance>{timestamps.join(' · ')}</Provenance>}
        <SourceList>
          {sources.map((source, index) => {
            const sourceUrl = toAbsoluteUrl(source.source, [source.source]);
            if (!sourceUrl) return null;
            const domain = source.domain || getSourceLabel(sourceUrl);
            const checkedAt = formatTimestamp(source.lastCheckedAt);
            return (
              <SourceItem key={source.eventSourceId ?? `${source.source}-${index}`}>
                <SourceLink href={sourceUrl} target="_blank" rel="noopener noreferrer">
                  {source.title || domain}
                  <ExternalLink size={13} aria-hidden="true" />
                </SourceLink>
                <SourceMeta>
                  <span>{domain}</span>
                  {(source.isOfficial || source.isVerified) && (
                    <TrustBadge>
                      <ShieldCheck size={11} aria-hidden="true" />
                      {source.isOfficial ? 'Official' : 'Verified'}
                    </TrustBadge>
                  )}
                  {checkedAt && <span>Checked {checkedAt}</span>}
                </SourceMeta>
              </SourceItem>
            );
          })}
        </SourceList>
      </Disclosure>
    </DetailSection>
  );
};

export default EventSourcesDisclosure;